import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    const savedCart = localStorage.getItem('mapleDaddiesCart');
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('mapleDaddiesCart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, quantity = 1) => {
    setCartItems(prevItems => {
      // Create unique key based on product id and variant
      const cartKey = product.selectedVariant 
        ? `${product.id}-${product.selectedVariant}` 
        : product.id;
      
      const existingItem = prevItems.find(item => {
        const itemKey = item.selectedVariant 
          ? `${item.id}-${item.selectedVariant}` 
          : item.id;
        return itemKey === cartKey;
      });
      
      if (existingItem) {
        return prevItems.map(item => {
          const itemKey = item.selectedVariant 
            ? `${item.id}-${item.selectedVariant}` 
            : item.id;
          return itemKey === cartKey
            ? { ...item, quantity: item.quantity + quantity }
            : item;
        });
      }
      return [...prevItems, { ...product, cartKey, quantity }];
    });
  };

  const removeFromCart = (cartKey) => {
    setCartItems(prevItems => prevItems.filter(item => {
      const itemKey = item.cartKey || item.id;
      return itemKey !== cartKey;
    }));
  };

  const updateQuantity = (cartKey, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartKey);
      return;
    }
    setCartItems(prevItems =>
      prevItems.map(item => {
        const itemKey = item.cartKey || item.id;
        return itemKey === cartKey ? { ...item, quantity } : item;
      })
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount
      }}
    >
      {children}
    </CartContext.Provider>
  );
};